import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  createEmptyHwcgEocNotice,
  type HwcgEocNoticeState,
} from 'components/IncidentConfig/CreateIncident/hwcgEocNotice';

/**
 * Fields seating and bulk send pass when the roster customization checkbox is
 * on.
 */
export type HwcgRosterNoticePayload = {
  locationLabel: string;
  location: HwcgEocNoticeState['location'];
  reportToTime: string;
  activateScript: string | null;
  confidentialityScript: string | null;
  channels: string[];
  activationMessages?: HwcgEocNoticeState['activationMessages'];
};

type HwcgRosterActivationContextValue = {
  customize: boolean;
  notice: HwcgEocNoticeState;
  setCustomize: (enabled: boolean) => void;
  setNotice: (next: HwcgEocNoticeState) => void;
  replaceNotice: (next: HwcgEocNoticeState) => void;
  /** Null while customization is off, so seating keeps the saved notice. */
  rosterNotice: HwcgRosterNoticePayload | null;
};

const HwcgRosterActivationContext =
  createContext<HwcgRosterActivationContextValue | null>(null);

export function HwcgRosterActivationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [customize, setCustomize] = useState(false);
  const [notice, setNoticeState] = useState<HwcgEocNoticeState>(
    createEmptyHwcgEocNotice
  );
  const edited = useRef(false);

  const setNotice = useCallback((next: HwcgEocNoticeState) => {
    edited.current = true;
    setNoticeState(next);
  }, []);

  const replaceNotice = useCallback((next: HwcgEocNoticeState) => {
    if (edited.current) return;
    setNoticeState(next);
  }, []);

  const rosterNotice = useMemo<HwcgRosterNoticePayload | null>(
    () =>
      customize
        ? {
            locationLabel: notice.locationLabel,
            location: notice.location,
            reportToTime: notice.reportToTime,
            activateScript: notice.activateScript,
            confidentialityScript: notice.confidentialityScript,
            channels: notice.channels,
            activationMessages: notice.activationMessages ?? {},
          }
        : null,
    [customize, notice]
  );

  const value = useMemo(
    () => ({
      customize,
      notice,
      setCustomize,
      setNotice,
      replaceNotice,
      rosterNotice,
    }),
    [customize, notice, setCustomize, setNotice, replaceNotice, rosterNotice]
  );

  return (
    <HwcgRosterActivationContext.Provider value={value}>
      {children}
    </HwcgRosterActivationContext.Provider>
  );
}

/** The customized notice, or null when the roster checkbox is off or absent. */
export function useHwcgRosterNoticePayload(): HwcgRosterNoticePayload | null {
  return useContext(HwcgRosterActivationContext)?.rosterNotice ?? null;
}

export function useHwcgRosterActivation() {
  return useContext(HwcgRosterActivationContext);
}
